<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import AppPopover from '@uiKit/elements/Popover.vue'
import NavbarProfileMenu from './NavbarProfileMenu.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'

withDefaults(
  defineProps<{
    sidebarCollapsed?: boolean
  }>(),
  { sidebarCollapsed: false },
)

const emit = defineEmits<{
  (e: 'toggle-sidebar'): void
  (e: 'toggle-collapse'): void
  (e: 'logout'): void
}>()

const profilePopover = ref<InstanceType<typeof AppPopover> | null>(null)
const router = useRouter()
const route = useRoute()

const notifications = ref([
  { id: 1, text: 'New Property Owner Application', time: '2m ago', unread: true },
  { id: 2, text: 'KYC Verification Pending', time: '1h ago', unread: true },
])

// ── Caption + title resolver ─────────────────────────────────────────────
// Maps a route to (section, title). Section is the SCREAMING text shown
// above the page title, e.g. "USERS · INVESTORS". Title is the bold page
// name shown below.
interface PageMeta {
  section: string
  title: string
}

const pageMetaMap: Record<string, PageMeta> = {
  '/dashboard': { section: 'DASHBOARD', title: 'Overview' },

  // Users — Investors
  '/investor': { section: 'USERS · INVESTORS', title: 'Investors' },
  '/investors/pipeline': { section: 'USERS · INVESTORS', title: 'Onboarding Pipeline' },
  '/investor-overview': { section: 'USERS · INVESTORS', title: 'Investor Overview' },
  '/investors/all': { section: 'USERS · INVESTORS', title: 'All investors' },
  '/investors/rejected': { section: 'USERS · INVESTORS', title: 'Rejected Investors' },
  '/investors/blocked': { section: 'USERS · INVESTORS', title: 'Blocked Investors' },
  '/investors/kyc-pending': { section: 'USERS · INVESTORS', title: 'KYC Pending' },
  '/investors/onboarding-review': { section: 'USERS · INVESTORS', title: 'Onboarding Review' },
  '/investors/bank-verifications': { section: 'USERS · INVESTORS', title: 'Bank Verifications' },
  '/investors/vasp-onboarding': { section: 'USERS · INVESTORS', title: 'VAISP Onboarding' },
  '/investors/kyc-expired': { section: 'USERS · INVESTORS', title: 'KYC Expired' },

  // Users — Property owners
  '/property-owners': { section: 'USERS · PROPERTY OWNERS', title: 'Property Owners' },
  '/property-owners/all': { section: 'USERS · PROPERTY OWNERS', title: 'All Property Owners' },
  '/property-owners/pending': { section: 'USERS · PROPERTY OWNERS', title: 'Onboarding Review' },
  '/property-owners/kyc-review': {
    section: 'USERS · PROPERTY OWNERS',
    title: 'KYC / KYB Review',
  },
  '/property-owners/bank-verifications': {
    section: 'USERS · PROPERTY OWNERS',
    title: 'Bank Verifications',
  },
  '/property-owners/vasp-onboarding': {
    section: 'USERS · PROPERTY OWNERS',
    title: 'VAISP Onboarding',
  },
  '/property-owners/kyc-expired': { section: 'USERS · PROPERTY OWNERS', title: 'KYC Expired' },
  '/property-owners/pipeline': { section: 'USERS · PROPERTY OWNERS', title: 'Onboarding Pipeline' },
  '/property-owners/rejected': { section: 'USERS · PROPERTY OWNERS', title: 'Rejected' },
  '/property-owners/blocked': { section: 'USERS · PROPERTY OWNERS', title: 'Blocked' },
  '/property-owners/create': { section: 'USERS · PROPERTY OWNERS', title: 'Create Owner' },
  '/property-owners/draft': { section: 'USERS · PROPERTY OWNERS', title: 'Draft Owners' },
  '/property-owners/bank-accounts': {
    section: 'USERS · PROPERTY OWNERS',
    title: 'Bank Accounts',
  },
  '/property-owners/bank-accounts/create': {
    section: 'USERS · PROPERTY OWNERS',
    title: 'Add bank account',
  },
  '/po-approvals': { section: 'USERS · PROPERTY OWNERS', title: 'Property Owners' },
  '/po-approvals/add': { section: 'USERS · PROPERTY OWNERS', title: 'Add Owner' },

  // Assets — Properties
  '/properties': { section: 'ASSETS · PROPERTIES', title: 'Properties' },
  '/properties/new': { section: 'ASSETS · PROPERTIES', title: 'New property' },
  '/properties/create': { section: 'ASSETS · PROPERTIES', title: 'Create property' },
  '/properties/archived': { section: 'ASSETS · PROPERTIES', title: 'Archived' },
  '/properties/verification': { section: 'ASSETS · PROPERTIES', title: 'Property verification' },
  '/properties/compliance-review': { section: 'ASSETS · PROPERTIES', title: 'Compliance review' },
  '/properties/financial-setup': { section: 'ASSETS · PROPERTIES', title: 'Financial setup' },
  '/properties/co-owner-kyc': { section: 'ASSETS · PROPERTIES', title: 'Co-owner KYCs' },
  '/properties/vasp-onboarding': { section: 'ASSETS · PROPERTIES', title: 'VAISP Onboarding' },
  '/properties/pipeline': { section: 'ASSETS · PROPERTIES', title: 'Onboarding pipeline' },
  '/properties/rejected': { section: 'ASSETS · PROPERTIES', title: 'Rejected & blocked' },
  // Post-approval queues — must be in the explicit map so the `/properties/:id`
  // dynamic-pattern fallback below doesn't catch them as "Property details".
  '/properties/rental-refresh': { section: 'ASSETS · PROPERTIES', title: 'Rental Refresh' },
  '/properties/valuation-refresh': {
    section: 'ASSETS · PROPERTIES',
    title: 'Valuation Refresh',
  },

  // Money — Rental Distribution
  '/rental-distributions/receipts': {
    section: 'MONEY · RENTAL DISTRIBUTION',
    title: 'Rent Receipts',
  },
  '/rental-distributions/runs': {
    section: 'MONEY · RENTAL DISTRIBUTION',
    title: 'Distribution Runs',
  },
  '/rental-distributions/ledger': {
    section: 'MONEY · RENTAL DISTRIBUTION',
    title: 'Property Rent Ledger',
  },
  '/rental-distributions/payouts': {
    section: 'MONEY · RENTAL DISTRIBUTION',
    title: 'Investor Payouts',
  },

  // Sales — Offerings
  '/offering-property': { section: 'SALES · OFFERINGS', title: 'Offerings' },
  '/offerings': { section: 'SALES · OFFERINGS', title: 'Offerings' },
  '/offerings/drafts': { section: 'SALES · OFFERINGS', title: 'Drafts' },
  '/offerings/pending-approval': { section: 'SALES · OFFERINGS', title: 'Pending approval' },
  '/offerings/vasp-onboarding': { section: 'SALES · OFFERINGS', title: 'VAISP Onboarding' },
  '/offerings/live': { section: 'SALES · OFFERINGS', title: 'Live offerings' },
  '/offerings/all': { section: 'SALES · OFFERINGS', title: 'All offerings' },
  '/offerings/closed': { section: 'SALES · OFFERINGS', title: 'Closed & cancelled' },

  // Sales — Orders
  '/orders': { section: 'SALES · ORDERS', title: 'Orders' },
  '/orders/pending-approval': { section: 'SALES · ORDERS', title: 'Pending approval' },
  '/orders/vasp-processing': { section: 'SALES · ORDERS', title: 'VAISP processing' },
  '/orders/all': { section: 'SALES · ORDERS', title: 'All orders' },
  '/orders/completed': { section: 'SALES · ORDERS', title: 'Completed' },
  '/orders/rejected': { section: 'SALES · ORDERS', title: 'Rejected' },
  '/orders/refund': { section: 'SALES · ORDERS', title: 'Refund' },
  '/tokenisation': { section: 'SALES · ORDERS', title: 'Tokenisation' },

  // Money — Treasury
  '/treasury': { section: 'MONEY · TREASURY', title: 'Treasury' },
  '/treasury/deposit-requests': { section: 'MONEY · TREASURY', title: 'Deposit approvals' },
  '/treasury/withdraw-requests': {
    section: 'MONEY · TREASURY',
    title: 'Withdrawal approvals',
  },
  '/treasury/refunds': { section: 'MONEY · TREASURY', title: 'Refund queue' },
  '/treasury/wallets': { section: 'MONEY · TREASURY', title: 'Investor wallets' },
  '/treasury/bank-accounts': { section: 'MONEY · TREASURY', title: 'Bank accounts' },
  '/treasury/accounts': { section: 'MONEY · TREASURY', title: 'Treasury accounts' },
  '/treasury/all': { section: 'MONEY · TREASURY', title: 'All transactions' },

  // Money — Crypto wallets
  '/wallets': { section: 'MONEY · CRYPTO WALLETS', title: 'Crypto wallets' },
  '/wallets/crypto-wallets': { section: 'MONEY · CRYPTO WALLETS', title: 'Crypto wallets' },
  '/wallets/crypto-wallets/vasp-onboarding': {
    section: 'MONEY · CRYPTO WALLETS',
    title: 'VAISP Onboarding',
  },
  '/wallets/crypto-wallets/management': {
    section: 'MONEY · CRYPTO WALLETS',
    title: 'Wallet management',
  },
  '/wallets/crypto-wallets/trustline-approvals': {
    section: 'MONEY · CRYPTO WALLETS',
    title: 'Trustline approvals',
  },
  '/wallets/crypto-wallets/failed-onchain': {
    section: 'MONEY · CRYPTO WALLETS',
    title: 'Failed on-chain ops',
  },
  '/wallets/all': { section: 'MONEY · CRYPTO WALLETS', title: 'All wallets' },
  '/wallets/transactions': {
    section: 'MONEY · CRYPTO WALLETS',
    title: 'On-chain transactions',
  },

  // Platform — Monitoring
  '/monitoring': { section: 'PLATFORM · MONITORING', title: 'Monitoring' },
  '/monitoring/infra': { section: 'PLATFORM · MONITORING', title: 'Infra dashboard' },
  '/monitoring/apm': { section: 'PLATFORM · MONITORING', title: 'APM' },
  '/monitoring/logs': { section: 'PLATFORM · MONITORING', title: 'Logs' },
  '/monitoring/integrations': { section: 'PLATFORM · MONITORING', title: 'Integration health' },
  '/monitoring/login-history': { section: 'PLATFORM · MONITORING', title: 'Login history' },

  // Platform — Settings
  '/settings': { section: 'PLATFORM · SETTINGS', title: 'Settings' },
  '/settings/general': { section: 'PLATFORM · SETTINGS', title: 'General' },
  '/settings/roles-permissions': { section: 'PLATFORM · SETTINGS', title: 'Roles & permissions' },
  '/settings/audit-logs': { section: 'PLATFORM · SETTINGS', title: 'Audit logs' },
  '/settings/system-configuration': {
    section: 'PLATFORM · SETTINGS',
    title: 'System configuration',
  },
  '/settings/notifications': { section: 'PLATFORM · SETTINGS', title: 'Notifications' },
  '/admins': { section: 'PLATFORM · TEAM & ACCESS', title: 'Admin users' },
  '/settings/admin-activity': { section: 'PLATFORM · SETTINGS', title: 'Admin activity log' },
  '/settings/countries': { section: 'PLATFORM · SETTINGS', title: 'Country configurations' },
  '/settings/fee-configuration': { section: 'PLATFORM · SETTINGS', title: 'Fee configuration' },
  '/settings/limits': { section: 'PLATFORM · SETTINGS', title: 'Limits & thresholds' },
  '/settings/templates': { section: 'PLATFORM · SETTINGS', title: 'Notification templates' },
  // Route path is under /masters, but it reads as a Settings screen in the nav —
  // the section label follows the sidebar, not the URL.
  '/masters/agreement-templates': {
    section: 'PLATFORM · SETTINGS',
    title: 'Agreement templates',
  },
  '/settings/sumsub': { section: 'PLATFORM · SETTINGS', title: 'Sumsub' },
  '/settings/ctrlalt': { section: 'PLATFORM · SETTINGS', title: 'VAISP (Ctrl Alt)' },
  '/settings/cma': { section: 'PLATFORM · SETTINGS', title: 'CMA bank' },
  '/settings/xrpl': { section: 'PLATFORM · SETTINGS', title: 'XRPL / Arva' },
  '/settings/dld': { section: 'PLATFORM · SETTINGS', title: 'DLD' },
  '/settings/apikeys': { section: 'PLATFORM · SETTINGS', title: 'API keys & webhooks' },
  '/masters': { section: 'PLATFORM · MASTERS', title: 'Master data' },

  // Search placeholders adapt to section context (Money / Platform handled below)
  // Misc
  '/profile': { section: 'SESSION', title: 'Profile' },
  '/help': { section: 'SESSION', title: 'Help & docs' },
}

const dynamicPatterns: Array<{ pattern: RegExp; section: string; title: string }> = [
  {
    // One investor's custody wallet — /wallets/crypto-wallets/management/:investorId.
    // The exact-path map above cannot carry it, so without this the detail page
    // renders with no breadcrumb at all.
    pattern: /^\/wallets\/crypto-wallets\/management\/[^/]+$/,
    section: 'MONEY · CRYPTO WALLETS',
    title: 'Investor wallet',
  },
  {
    pattern: /^\/properties\/edit\/[^/]+$/,
    section: 'ASSETS · PROPERTIES',
    title: 'Edit property',
  },
  {
    // Resume an existing draft — /properties/:id/create. Must precede the
    // generic /properties/:id pattern so the wizard isn't labelled "details".
    pattern: /^\/properties\/[^/]+\/create$/,
    section: 'ASSETS · PROPERTIES',
    title: 'Resume draft',
  },
  {
    pattern: /^\/properties\/[^/]+$/,
    section: 'ASSETS · PROPERTIES',
    title: 'Property details',
  },
  {
    pattern: /^\/offering-property\/[^/]+$/,
    section: 'SALES · OFFERINGS',
    title: 'Offering details',
  },
  {
    pattern: /^\/investor-details\/[^/]+$/,
    section: 'USERS · INVESTORS',
    title: 'Investor Details',
  },
  {
    // Resume a saved draft owner — must precede the generic /property-owners/:id
    // pattern so the edit wizard isn't labelled "Property Owner Details".
    pattern: /^\/property-owners\/draft\/edit\/[^/]+$/,
    section: 'USERS · PROPERTY OWNERS',
    title: 'Edit Draft Owner',
  },
  {
    pattern: /^\/property-owners\/[^/]+$/,
    section: 'USERS · PROPERTY OWNERS',
    title: 'Property Owner Details',
  },
  {
    pattern: /^\/po-approvals\/edit\/[^/]+$/,
    section: 'USERS · PROPERTY OWNERS',
    title: 'Edit owner',
  },
  {
    pattern: /^\/po-approvals\/[^/]+$/,
    section: 'USERS · PROPERTY OWNERS',
    title: 'Owner details',
  },
]

const pageMeta = computed<PageMeta>(() => {
  const path = route.path
  if (pageMetaMap[path]) return pageMetaMap[path]
  for (const dp of dynamicPatterns) {
    if (dp.pattern.test(path)) return { section: dp.section, title: dp.title }
  }
  return { section: '', title: '' }
})

// ── Breadcrumb trail ─────────────────────────────────────────────────────
// The section string (e.g. "USERS · INVESTORS") is uppercase for the old
// caption style; title-case each segment for the breadcrumb. The page title
// is already correctly cased (keeps acronyms like "VASP" / "KYC"), so it is
// appended verbatim as the final, active crumb.
const toTitleCase = (value: string): string =>
  value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())

const breadcrumbs = computed<string[]>(() => {
  const meta = pageMeta.value
  if (!meta.title) return []
  const sectionCrumbs = meta.section
    ? meta.section
        .split('·')
        .map((part) => toTitleCase(part.trim()))
        .filter(Boolean)
    : []
  return [...sectionCrumbs, meta.title]
})

// ── Avatar initials from logged-in user (fallback "NA") ──────────────────
const initials = computed(() => 'NA')
</script>

<template>
  <Box is="header" class="app-header">
    <!-- Left: hamburger + caption/title -->
    <Box class="header-left">
      <Box
        is="button"
        type="button"
        class="header-btn collapse-toggle"
        :title="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        @click="$emit('toggle-collapse')"
      >
        <PiIcon name="bars" />
      </Box>

      <Box
        is="button"
        type="button"
        class="header-btn mobile-menu-toggle"
        aria-label="Open sidebar"
        @click="$emit('toggle-sidebar')"
      >
        <PiIcon name="bars" />
      </Box>

      <Box v-if="breadcrumbs.length" flex align="center" gap="2" class="breadcrumbs">
        <template v-for="(crumb, index) in breadcrumbs" :key="index">
          <PiIcon v-if="index > 0" name="chevron-right" class="breadcrumb-sep" />
          <Text
            is="span"
            type="body"
            :weight="index === breadcrumbs.length - 1 ? 'semibold' : 'medium'"
            :color="index === breadcrumbs.length - 1 ? 'default' : 'muted'"
            class="breadcrumb-crumb"
            :class="{ 'breadcrumb-crumb--active': index === breadcrumbs.length - 1 }"
            >{{ crumb }}</Text
          >
        </template>
      </Box>
    </Box>

    <!-- Right: icons + avatar -->
    <Box class="header-right">
      <Box is="button" type="button" class="header-btn header-btn--bell" title="Notifications">
        <PiIcon name="bell" />
        <Text
          v-if="notifications.length > 0"
          is="span"
          type="caption"
          class="notification-badge"
          aria-label="Unread notifications"
        >
          {{ notifications.length > 9 ? '9+' : notifications.length }}
        </Text>
      </Box>

      <Box
        is="button"
        type="button"
        class="avatar-btn"
        aria-label="Profile"
        @click="profilePopover?.toggle($event)"
      >
        {{ initials }}
      </Box>
    </Box>

    <AppPopover ref="profilePopover">
      <NavbarProfileMenu
        @navigate="
          (to) => {
            profilePopover?.hide()
            router.push(to)
          }
        "
        @logout="
          () => {
            profilePopover?.hide()
            emit('logout')
          }
        "
      />
    </AppPopover>
  </Box>
</template>

<style scoped>
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 1.25rem;
  gap: 1rem;
  background: var(--p-surface-0);
  border-bottom: 1px solid var(--p-content-border-color);
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 100;
}

/* ── Left ── */
.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
}

.header-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: var(--p-text-muted-color);
  flex-shrink: 0;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
  position: relative;
}

.header-btn:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.header-btn--bordered {
  border: 1px solid var(--p-content-border-color);
}

.header-btn--bordered:hover {
  border-color: var(--p-primary-300);
}

.header-btn--bell {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: transparent;
}

.header-btn--bell:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.header-btn--bell:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.mobile-menu-toggle {
  display: none;
}

/* ── Breadcrumb trail ── */
.breadcrumbs {
  min-width: 0;
  overflow: hidden;
}

.breadcrumb-sep {
  font-size: 11px;
  color: var(--p-text-muted-color);
  flex-shrink: 0;
  opacity: 0.7;
}

.breadcrumb-crumb {
  white-space: nowrap;
  flex-shrink: 0;
}

.breadcrumb-crumb--active {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Right ── */
.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.625rem;
  flex-shrink: 0;
}

.notification-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  background: var(--p-danger-500);
  color: var(--p-surface-0);
  font-size: 9px;
  font-weight: 600;
  min-width: 14px;
  height: 14px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
  border: 1.5px solid var(--p-surface-0);
}

/* ── Avatar button ──
   Neutral dark-monochrome identity chip. The avatar is the operator's
   identity, not a primary CTA — never tint it with the brand accent.
*/
.avatar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: var(--p-sliceal-dark-slate);
  color: var(--p-primary-contrast-color);
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  letter-spacing: 0.04em;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.15s ease,
    transform 0.15s ease;
}

.avatar-btn:hover {
  background: var(--p-sliceal-dark-slate-hover);
}

.avatar-btn:active {
  transform: scale(0.96);
}

/* ── Responsive ── */
@media (max-width: 991px) {
  .collapse-toggle {
    display: none;
  }

  .mobile-menu-toggle {
    display: flex;
  }

  /* On mobile, collapse the trail to the active (last) crumb only — the
     parent sections eat horizontal room the phone layout doesn't have. */
  .breadcrumb-crumb:not(.breadcrumb-crumb--active),
  .breadcrumb-sep {
    display: none;
  }
}
</style>
